import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { AsyncRequestService } from 'src/app/core/async-request/async-request.service';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { AgentShortcutService } from 'src/app/shared/components/agent-shortcuts-modal/agent-shortcut.service';
import { SearchBarService } from 'src/app/shared/components/search-bar/search-bar.service';
import { WABAAssociation } from 'src/app/shared/components/select-whatsapp-waba/waba-association.model';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { SiteTypeEnum } from 'src/app/shared/enums';
import { SiteV2 } from 'src/app/shared/models';
import { AttachmentMediaTypeEnum } from 'src/app/shared/models/misc.model';
import { DateTimezonePipe } from 'src/app/shared/pipes/date-timezone.pipe';
import { API_URL_COMPANIES, API_URL_COMPANIES_V2, BasicService } from 'src/app/shared/services/basic.service';
import { ErrorHandlingService } from 'src/app/shared/services/error-handling.service';
import { WhatsappService } from 'src/app/shared/strategies/channels';
import { WhatsappTemplate as WhatsappTemplateProcessingPage } from 'src/app/task-process/shared/models';
import { Template, WhatsAppTemplateStatusEnum, WhatsappTemplate } from './shared';
import { WhatsappTemplateButtonTypeEnum } from './shared/models/whatsapp-template-button-type.enum';
import { WhatsappTemplateButton } from './shared/models/whatsapp-template-button.model';
import { WhatsappTemplateForm } from './shared/models/whatsapp-template-form.model';
import { WhatsappTemplateScenarioDependency } from './shared/models/whatsapp-template-scenario-dependency.model';
import { WhatsappTemplateSettings } from './shared/models/whatsapp-template-settings.model';
import { supportedLanguages } from './shared/supported-languages.const';

@Injectable({
  providedIn: 'root',
})
export class WhatsappTemplatesService {
  whatsappAccounts: SiteV2[] = [];
  headerMediaFileBS: BehaviorSubject<{ fileHandle: string; media_url: string; file: File; mediaType: string }> =
    new BehaviorSubject(null);
  createTemplateSiteIdUpdatedBS: BehaviorSubject<number> = new BehaviorSubject(null);
  cleanInvalidAgentShortcutsFromQuickRepliesBS: BehaviorSubject<Set<string>> = new BehaviorSubject(null);
  canActivateLimitedTimeOfferBS: BehaviorSubject<boolean> = new BehaviorSubject(null);

  wabas: WABAAssociation[] = [];

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private asyncRequestService: AsyncRequestService,
    private basicService: BasicService,
    private dateTimezonePipe: DateTimezonePipe,
    private errorHandlingService: ErrorHandlingService,
    private agentShortcutService: AgentShortcutService,
    private httpClient: HttpClient,
    private sitesService: SitesService,
    private searchBarService: SearchBarService
  ) {}

  findAccounts() {
    if (this.whatsappAccounts?.length) {
      return of(this.whatsappAccounts);
    }
    const whatsappAccountType = new WhatsappService().getType();

    this.whatsappAccounts = this.sitesService.sitesV2
      .filter(account => account.type === whatsappAccountType && !account.isWhiteListed)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return of(this.whatsappAccounts);
  }

  findAll(): Observable<WhatsappTemplate[]> {
    return this.findAccounts().pipe(
      switchMap(accounts => {
        return this.httpClient
          .get<Template[]>(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/whatsapp/answer-templates`)
          .pipe(map(templates => this.formatTemplates(templates, accounts)));
      })
    );
  }

  findOne(siteId: number, elementName: string) {
    let params = new HttpParams();
    params = params.append('element_name', elementName);
    return this.findAccounts().pipe(
      switchMap(accounts => {
        return this.httpClient
          .get<Template[]>(
            `${API_URL_COMPANIES_V2}/${this.alcmeonContextService.companyId}/sites/${siteId}/whatsapp/answer-templates`,
            { params }
          )
          .pipe(
            map(templates => this.formatTemplates(templates, accounts)[0]),
            this.errorHandlingService.catchAlertAndThrowHttpError()
          );
      })
    );
  }

  findTemplateUsageDependencies(
    siteId: number,
    elementName: string,
    language?: string
  ): Observable<WhatsappTemplateScenarioDependency[]> {
    let params = new HttpParams();
    params = params.append('element_name', elementName);
    if (language) {
      params = params.append('language', language);
    }
    return this.httpClient
      .get<WhatsappTemplateScenarioDependency[]>(
        `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/sites/${siteId}/whatsapp/answer-templates-dependencies`,
        { params }
      )
      .pipe(catchError(() => []));
  }

  remove(siteId: number, elementName: string) {
    let params = new HttpParams();
    params = params.append('element_name', elementName);
    return this.httpClient
      .delete(
        `${API_URL_COMPANIES_V2}/${this.alcmeonContextService.companyId}/sites/${siteId}/whatsapp/answer-templates`,
        { params }
      )
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  private formatTemplates = (templates: Template[], accounts: SiteV2[]): WhatsappTemplate[] => {
    const formatted = templates.reduce((aggregation, template) => {
      const key = `${template.name}|${template.site_id}`;
      aggregation.set(key, (aggregation.get(key) || []).concat(template));
      return aggregation;
    }, new Map<string, Template[]>());
    const templatesAggregatted: WhatsappTemplate[] = [];
    formatted.forEach((languages, key) => {
      const [name] = key.split('|');
      const category = languages.find(language => language.category)?.category;
      const lastModificationDate = (languages || []).sort((a, b) =>
        moment(a.last_modified_at).isSameOrAfter(b.last_modified_at) ? -1 : 1
      )[0]?.last_modified_at;
      const { status, site_id, usages } = languages[0];
      const isWhatsappCloudAPI = this.sitesService.sitesV2Map.get(site_id)?.isCloudAPI;
      let languagesLabel = '';
      let texts = '';
      languages.forEach(item => {
        item.languageLabel = this.basicService.translate(item.language);
        languagesLabel += `#${item.languageLabel}`;
        texts += `#${item.text}`;
      });
      const formatLabel = (value, key) => (value ? this.basicService.translate(key, value) : 'N/C');
      const categoryLabel = formatLabel(category, `parameters/whatsapp-templates/category/${category}/label`);
      const statusLabel = formatLabel(status, `parameters/whatsapp-templates/status/${status}`);
      const lastModificationDateLabel = lastModificationDate?.length
        ? this.dateTimezonePipe.transform(lastModificationDate, 'shortDate')
        : '';

      const accountName = accounts.find(a => a.id === site_id)?.name || 'N/C';
      let isElementEditable = false;
      const templates = (languages || []).map(l => {
        const { text, language, displayed_placeholders, buttons, header, limited_time_offer, footer, isEditable } = l;
        isElementEditable = isEditable;
        const buttonsFiltered = isWhatsappCloudAPI
          ? [].concat(buttons || [])
          : (buttons || []).filter(({ type }) => type !== WhatsappTemplateButtonTypeEnum.COPY_CODE);
        const buttonsFormatted = buttonsFiltered.map((button: WhatsappTemplateButton) => {
          if (typeof button === 'string') {
            return { type: WhatsappTemplateButtonTypeEnum.QUICK_REPLY, text: button } as WhatsappTemplateButton;
          }
          this.formatButtonUrl(button, isWhatsappCloudAPI);

          return button;
        });
        Object.keys(displayed_placeholders || {}).forEach(key => {
          const { label, example } = displayed_placeholders[key];
          if (!label && !example) {
            displayed_placeholders[key] = {
              label: `${displayed_placeholders[key]}`,
              example: '',
            };
          }
        });

        Object.keys(displayed_placeholders || {}).forEach(key => {
          const { label, example } = displayed_placeholders[key];
          let labelFormatted = label;
          try {
            // Meta returns an array as string.
            labelFormatted = label.replace(/^\[*|\]*$/g, '');
          } catch {
            // Do nothing
          }
          displayed_placeholders[key] = { label: labelFormatted, example };
        });

        return {
          code: language,
          active: true,
          template: {
            header: header || {
              text: undefined,
              type: 'text',
            },
            limitedTimeOfferEnabled: !!limited_time_offer,
            limited_time_offer,
            body: text,
            footer,
            buttons: buttonsFormatted,
            placeholders: displayed_placeholders,
          },
        };
      });

      const formatDate = (date: string): string => this.dateTimezonePipe.transform(date, 'short');
      const metadata = {
        ...languages[0],
        languages,
        detailsUrl: this.getTemplateDetailsPath(`${name}`, site_id),
        category,
        categoryLabel,
        statusLabel,
        lastModificationDate,
        lastModificationDateLabel,
        accountName,
        summary: `${name}#${accountName}#${categoryLabel}#${statusLabel}#${lastModificationDateLabel}${languagesLabel}${texts}`,
        created_by: languages[0].created_by,
        last_modified_by: languages[0].last_modified_by,
        created_at: formatDate(languages[0].created_at),
        last_modified_at: formatDate(languages[0].last_modified_at),
        usages,
      };
      templatesAggregatted.push({
        metadata,
        isElementEditable,
        // Same model than the form.
        main: {
          name,
          languages: (languages || []).map(({ language }) => language),
          siteId: site_id,
          status,
          usages,
        },
        category,
        templates,
      });
    });
    return templatesAggregatted;
  };

  save(body: WhatsappTemplateForm, create?: boolean): Observable<any> {
    const { siteId } = body.main;
    const isWhatsappCloudAPI = this.sitesService.sitesV2Map.get(siteId)?.isCloudAPI;

    (body.templates || []).forEach(({ template }: { template: any }) => {
      if (!isWhatsappCloudAPI) {
        template.buttons = (template.buttons || []).filter(
          ({ type }) => type !== WhatsappTemplateButtonTypeEnum.COPY_CODE
        );
      }
      template.buttons.forEach(button => this.formatButtonUrl(button, isWhatsappCloudAPI));
    });
    if (create) {
      return this.httpClient
        .post(
          `${API_URL_COMPANIES_V2}/${this.alcmeonContextService.companyId}/sites/${siteId}/whatsapp/answer-templates`,
          body
        )
        .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
    }
    return this.httpClient
      .put(
        `${API_URL_COMPANIES_V2}/${this.alcmeonContextService.companyId}/sites/${siteId}/whatsapp/answer-templates`,
        body
      )
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  getTemplateDetailsPath(name: string, siteId?: number) {
    const path = `/#/${this.alcmeonContextService.companyId}/fuse/parameters/message-templates/whatsapp/${siteId}/template/${name}`;
    return this.basicService.fullURLForRoutePath(path);
  }

  private formatButtonUrl = (button: WhatsappTemplateButton, isWhatsappCloudAPI: boolean) => {
    if (!button) {
      return;
    }
    if (button.type === WhatsappTemplateButtonTypeEnum.URL) {
      delete button['track_link_label'];
      if (Array.isArray(button.example) && button.example[0]) {
        button.example = button.example[0] as any;
      }
      if (button.example === null) {
        delete button.example;
      }
      if (!isWhatsappCloudAPI) {
        delete button['track_link_label'];
        delete button.track_link_label_id;
      }
    } else if (button.type === WhatsappTemplateButtonTypeEnum.COPY_CODE) {
      delete button.text;
    }
  };

  /**
   * Allows to show the details for a given template.
   * In case of new template it uses `create`.
   * @remarks It uses a not recommended mechanism due to the hybrid architecture and the communication between the both version of Angular.
   * @param id the id to reach.
   */
  /**
   * @deprecated To remove once angularjs is fully migrated.
   */
  navigateToDetails(id: string, siteId: number) {
    this.basicService.window.location.href = this.getTemplateDetailsPath(id, siteId);
  }

  navigateToCreate() {
    const path = `/#/${this.alcmeonContextService.companyId}/fuse/parameters/message-templates/whatsapp/template`;
    this.basicService.window.location.href = this.basicService.fullURLForRoutePath(path);
  }

  synchronizeStatuses() {
    return this.asyncRequestService
      .post(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/whatsapp/answer-templates/update`, {})
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  /**
   * Allows to show the templates.
   * @deprecated To remove once angularjs is fully migrated.
   */
  navigateToList() {
    const path = `/#/${this.alcmeonContextService.companyId}/fuse/parameters/message-templates?type=${SiteTypeEnum.WhatsApp}`;
    const url = this.basicService.fullURLForRoutePath(path);
    this.basicService.window.location.href = url;
  }

  getSupportedLanguages() {
    return supportedLanguages
      .map(l => {
        const result = this.basicService.translate(l.code);
        const label = result === l.code ? l.label : result;
        return { id: l.code, label };
      })
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }

  getLimitations(): { maxLength; maxNumber } {
    return {
      maxLength: {
        templateName: 512,
        header: 60,
        body: 1024,
        textLTO: 16,
        bodyLTO: 600,
        footer: 60,
        button: 25,
        url: 1500, // url + example should not exceed 2000 caracters.
        example: 500,
        copyCode: 15,
      },
      maxNumber: {
        button: 10,
        placeholders: 5,
      },
    };
  }

  uploadMediaForTemplate(
    siteId: number,
    file: File,
    mediaType: AttachmentMediaTypeEnum
  ): Observable<{
    fileHandle: string;
    staticMediaId: number;
    staticMediaUrl: string;
  }> {
    if (!file) {
      return;
    }
    const { type, name } = file;
    const formData = new FormData();

    formData.set('mediaType', mediaType);
    formData.set('mimeType', type);
    formData.set('filename', name);
    formData.set('file', file);

    return this.httpClient.post<any>(
      `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/sites/${siteId}/whatsapp/answer-templates/header-media`,
      formData
    );
  }

  uploadMediaForMessage(
    siteId: number,
    file: File,
    mediaType: AttachmentMediaTypeEnum
  ): Observable<{ headerMediaUrl: string; headerMediaFilename: string }> {
    if (!file) {
      return;
    }
    const { type, name } = file;
    const formData = new FormData();

    formData.set('mediaType', mediaType);
    formData.set('mimeType', type);
    formData.set('filename', name);
    formData.set('file', file);

    return this.httpClient.post<any>(
      `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/sites/${siteId}/whatsapp/answer-templates/temporary-header-media`,
      formData
    );
  }

  uploadMediaForBot(
    templateId: number,
    siteId: number,
    scenarioId: number,
    boxId: number,
    file: File
  ): Observable<{ headerMediaUrl: string }> {
    const { type, name } = file;
    const formData = new FormData();

    formData.set('mediaType', this.getPseudoMediaType(type));
    formData.set('filename', name);
    formData.set('file', file);

    return this.httpClient.post<any>(
      `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/sites/${siteId}/whatsapp/answer-templates/${templateId}/scenarios/${scenarioId}/box/${boxId}/header-media`,
      formData
    );
  }

  private getPseudoMediaType(mediaType: string) {
    if (mediaType.startsWith('image/')) {
      return 'image';
    }
    if (mediaType.startsWith('video/')) {
      return 'video';
    }
    if (mediaType === 'application/pdf') {
      return 'document';
    }
  }

  getAnswerTemplatesForQuickReplies(siteId: number): Observable<WhatsappTemplateProcessingPage[]> {
    return this.httpClient.get<WhatsappTemplateProcessingPage[]>(
      `${API_URL_COMPANIES_V2}/${this.alcmeonContextService.companyId}/sites/${siteId}/whatsapp/answer-templates?usage=bot`
    );
  }
  getAnswerTemplatesForProcessingPage(siteId: number): Observable<WhatsappTemplateProcessingPage[]> {
    return this.httpClient
      .get<WhatsappTemplateProcessingPage[]>(
        `${API_URL_COMPANIES_V2}/${this.alcmeonContextService.companyId}/sites/${siteId}/whatsapp/answer-templates?usage=advisor`
      )
      .pipe(
        map(response => {
          response.forEach(whatsappTemplate => {
            whatsappTemplate.textWithReplacedPlaceholders = whatsappTemplate.text;
            whatsappTemplate.parameters = [];
            whatsappTemplate.placeholders = whatsappTemplate.displayed_placeholders;
            for (const key of Object.keys(whatsappTemplate.placeholders || {})) {
              const index = Number(key.match(/{{([0-9]+)}}/)[1]) - 1; // placeholders start at {{1}}
              whatsappTemplate.parameters[index] = {
                placeholder: whatsappTemplate.placeholders[key]?.label || (whatsappTemplate.placeholders[key] as any),
                value: '',
              };
            }
          });
          return response.filter(({ status }) => status === WhatsAppTemplateStatusEnum.approved);
        })
      );
  }

  getAnswerTemplatesForEngagement(siteId: number): Observable<WhatsappTemplateProcessingPage[]> {
    return this.httpClient
      .get<WhatsappTemplateProcessingPage[]>(
        `${API_URL_COMPANIES_V2}/${this.alcmeonContextService.companyId}/sites/${siteId}/whatsapp/answer-templates?usage=advisor`
      )
      .pipe(
        map(response => {
          (response || []).forEach(whatsappTemplate => {
            whatsappTemplate.textWithReplacedPlaceholders = whatsappTemplate.text;
            whatsappTemplate.parameters = [];
            (whatsappTemplate.buttons || []).forEach(button => {
              if (button.type === WhatsappTemplateButtonTypeEnum.COPY_CODE) {
                button.example = [button.example as string];
              }
            });
            whatsappTemplate.placeholders = whatsappTemplate.displayed_placeholders;
            for (const key of Object.keys(whatsappTemplate.placeholders || {})) {
              const index = Number(key.match(/{{([0-9]+)}}/)[1]) - 1; // placeholders start at {{1}}
              whatsappTemplate.parameters[index] = {
                placeholder: whatsappTemplate.placeholders[key]?.label || (whatsappTemplate.placeholders[key] as any),
                value: '',
              };
            }
          });
          return (response || []).filter(({ status }) => status === WhatsAppTemplateStatusEnum.approved);
        })
      );
  }

  getWABAAssociations(siteId: number): Observable<WABAAssociation[]> {
    if (this.wabas?.length) {
      return of(this.wabas);
    }

    return this.httpClient
      .get<Choice[]>(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/sites/${siteId}/waba`)
      .pipe(
        this.errorHandlingService.catchAlertAndThrowHttpError(),
        catchError(() => of([])),
        map(result => {
          const wabas = (result || []).sort((a, b) => (a.label || '').localeCompare(b.label || ''));
          this.wabas = wabas;
          return wabas;
        })
      );
  }

  forceWhatsAppTemplateMode(): boolean {
    return localStorage.getItem('force-whatsapp-template-mode') === 'true';
  }

  async getAgentShortcutsForAccount(siteId: number) {
    if (!siteId) {
      return;
    }
    return await this.agentShortcutService
      .getAgentShortcuts(this.alcmeonContextService.companyId, siteId)
      .pipe(map(agentShortcuts => agentShortcuts.map(({ element_id, label }) => ({ id: element_id, label }))))
      .toPromise();
  }

  retrieveTemplates = (): Observable<HttpResponse<WhatsappTemplateSettings[]>> => {
    const body = this.searchBarService.buildCriteria();
    return this.httpClient
      .post<WhatsappTemplateSettings[]>(
        `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/whatsapp/answer-templates`,
        body,
        {
          observe: 'response',
        }
      )
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  };

  getUrlButtonTrackLinkLabels() {
    return this.httpClient
      .post<Choice[]>(`${API_URL_COMPANIES_V2}/${this.alcmeonContextService.companyId}/parameters/track-link-labels`, {
        sort: {
          active: 'label',
          direction: 'desc',
        },
        start: 0,
        range: 99999,
      })
      .pipe(
        catchError(() => of([])),
        map(result => (result || []).sort((a, b) => (a?.label || '').localeCompare(b?.label || '')))
      );
  }
}
